/**
 * Session form (information meeting).
 */
import * as React from 'react';
import { Calendar, Users, BookOpen, X } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { Textarea } from '@/components/forms/Textarea';
import { Checkbox } from '@/components/forms/Checkbox';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { DatePicker } from '@/components/forms/DatePicker';
import { TimePicker } from '@/components/forms/TimePicker';
import { LabelBadge } from '@/components/common/LabelBadge';
import { useAuthStore } from '@/stores/authStore';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { toLocalDateString } from '@/utils/format-date-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createSession, getSession, updateSession } from '@/services/inperson/sessions.service';
import {
  INFORMATION_MEETING_ANIMATOR_ROLE_CODES,
  INFORMATION_MEETING_MAX_PARTICIPANTS,
  INFORMATION_MEETING_PARTICIPANT_ROLE_CODES,
  SESSION_TYPE_CONFIG,
} from '@/components/sessions/session-configs';
import type { Session } from '@/types/inperson/session.types';
import {
  addOneHour,
  buildInformationMeetingCreateDto,
  buildInformationMeetingUpdateDto,
  deriveInformationMeetingFormFlags,
  getMinEndTime,
  getMinStartTimeForSessionDate,
  informationMeetingSessionToFormState,
  syncInformationMeetingVolunteers,
  toSelectedUser,
} from '@/components/sessions/inperson/session-form-utils';
import type { SelectedUser } from '@/types/user.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link InformationMeetingForm}. */
interface InformationMeetingFormProps {
  mode: 'create' | 'edit';
  sessionId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Session form (information meeting). */
export const InformationMeetingForm: React.FC<InformationMeetingFormProps> = ({
  mode,
  sessionId,
  onClose,
  onSuccess,
}) => {
  const isEdit = mode === 'edit';
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => getSession(sessionId!),
    enabled: isEdit && !!sessionId,
  });

  const [sessionDate, setSessionDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [selectedAnimators, setSelectedAnimators] = React.useState<SelectedUser[]>([]);
  const [animatorValue, setAnimatorValue] = React.useState('');
  const [animatorResetKey, setAnimatorResetKey] = React.useState(0);
  const [includeCurrentUserAsAnimator, setIncludeCurrentUserAsAnimator] = React.useState(false);
  const [maxParticipants, setMaxParticipants] = React.useState(
    INFORMATION_MEETING_MAX_PARTICIPANTS,
  );
  const [selectedTutorParticipants, setSelectedTutorParticipants] = React.useState<SelectedUser[]>(
    [],
  );
  const [tutorParticipantValue, setTutorParticipantValue] = React.useState('');
  const [tutorParticipantResetKey, setTutorParticipantResetKey] = React.useState(0);
  const [internalNotes, setInternalNotes] = React.useState('');
  const [initialized, setInitialized] = React.useState(false);

  const {
    options: animatorOptions,
    isLoading: animatorsLoading,
    onSearch: onAnimatorSearch,
  } = useAdminAutocomplete({ roleCodes: [...INFORMATION_MEETING_ANIMATOR_ROLE_CODES] });

  const {
    options: tutorParticipantOptions,
    isLoading: tutorParticipantsLoading,
    onSearch: onTutorParticipantSearch,
  } = useAdminAutocomplete({ roleCodes: [...INFORMATION_MEETING_PARTICIPANT_ROLE_CODES] });

  React.useEffect(() => {
    if (!isEdit || !session || initialized) return;
    const hydrated = informationMeetingSessionToFormState(session);
    setSessionDate(hydrated.sessionDate);
    setStartTime(hydrated.startTime);
    setEndTime(hydrated.endTime);
    setMaxParticipants(hydrated.maxParticipants);
    setInternalNotes(hydrated.internalNotes);
    setSelectedAnimators(hydrated.selectedAnimators);
    setSelectedTutorParticipants(hydrated.selectedTutorParticipants);
    setInitialized(true);
  }, [isEdit, session, initialized]);

  React.useEffect(() => {
    if (!user) return;
    const meEntry = toSelectedUser(user);
    if (includeCurrentUserAsAnimator && !selectedAnimators.some((a) => a.id === user.id)) {
      setSelectedAnimators((prev) => [meEntry, ...prev]);
    } else if (!includeCurrentUserAsAnimator) {
      setSelectedAnimators((prev) => prev.filter((a) => a.id !== user.id));
    }
  }, [includeCurrentUserAsAnimator, user]);

  /** Changing start time clamps to now when today, then fills end with start + 1h. */
  const handleStartTimeChange = (value: string) => {
    const minStart = getMinStartTimeForSessionDate(sessionDate);
    const nextStart = minStart && value && value < minStart ? minStart : value;
    setStartTime(nextStart);
    if (nextStart) setEndTime(addOneHour(nextStart));
  };

  /** Today-only: drop a start that would now sit in the past. */
  function handleSessionDateChange(value: string): void {
    setSessionDate(value);
    const minStart = getMinStartTimeForSessionDate(value);
    if (minStart && startTime && startTime < minStart) {
      setStartTime('');
      setEndTime('');
    }
  }

  /** Add an animator from the {@link Select} (skip if already listed). */
  const handleAnimatorChange = (value: string) => {
    const selected = animatorOptions.find((o) => o.value === value);
    if (selected) {
      if (!selectedAnimators.some((a) => a.id === value)) {
        setSelectedAnimators((prev) => [...prev, { id: value, label: selected.label }]);
      }
      setAnimatorValue('');
      setAnimatorResetKey((k) => k + 1);
    } else {
      setAnimatorValue(value);
    }
  };

  /** Remove an animator chip; clearing self also unchecks include-me. */
  const handleRemoveAnimator = (userId: string) => {
    setSelectedAnimators((prev) => prev.filter((a) => a.id !== userId));
    if (userId === user?.id) setIncludeCurrentUserAsAnimator(false);
  };

  /** Append a unique tutor participant, capped by `maxParticipants`. */
  const handleTutorParticipantChange = (value: string) => {
    if (selectedTutorParticipants.length >= maxParticipants) {
      toast.error(getErrorMessage('participantLimitReached'));
      return;
    }
    const selected = tutorParticipantOptions.find((o) => o.value === value);
    if (selected) {
      if (!selectedTutorParticipants.some((t) => t.id === value)) {
        setSelectedTutorParticipants((prev) => [...prev, { id: value, label: selected.label }]);
      }
      setTutorParticipantValue('');
      setTutorParticipantResetKey((k) => k + 1);
    } else {
      setTutorParticipantValue(value);
    }
  };

  /** Remove a tutor-participant chip. */
  const handleRemoveTutorParticipant = (userId: string) => {
    setSelectedTutorParticipants((prev) => prev.filter((t) => t.id !== userId));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!sessionDate) throw new Error(getErrorMessage('sessionDateRequired'));
      if (!startTime) throw new Error(getErrorMessage('startTimeRequired'));

      if (!isEdit) {
        const dto = buildInformationMeetingCreateDto({
          sessionDate,
          startTime,
          endTime,
          maxParticipants,
          internalNotes,
          volunteerIds: selectedTutorParticipants.map((t) => t.id),
          animatorIds: selectedAnimators.map((a) => a.id),
        });
        const result = await createSession(dto);
        return Array.isArray(result) ? (result as Session[])[0] : (result as Session);
      }

      if (!session || !sessionId) throw new Error(getErrorMessage('sessionNotLoaded'));
      await updateSession(
        sessionId,
        buildInformationMeetingUpdateDto({
          sessionDate,
          startTime,
          endTime,
          animatorIds: selectedAnimators.map((a) => a.id),
          maxParticipants,
          internalNotes,
        }),
      );
      await syncInformationMeetingVolunteers(
        sessionId,
        session.participants,
        selectedTutorParticipants,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sessions'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['session-groups'], refetchType: 'all' }),
        ...(isEdit && sessionId
          ? [
              queryClient.invalidateQueries({
                queryKey: ['session', sessionId],
                refetchType: 'all',
              }),
            ]
          : []),
      ]);
      toast.success(
        isEdit
          ? "Réunion d'information modifiée avec succès"
          : "Réunion d'information créée avec succès",
      );
      onSuccess();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const { isTerminal, participantLimitReached, isFormValid } = deriveInformationMeetingFormFlags({
    isEdit,
    sessionStatus: session?.status,
    selectedTutorCount: selectedTutorParticipants.length,
    maxParticipants,
    sessionDate,
    startTime,
    endTime,
  });
  const todayStr = toLocalDateString(new Date());
  const typeConfig = session ? SESSION_TYPE_CONFIG[session.type] : undefined;
  const canIncludeCurrentUser = Boolean(user?.email);

  return (
    <CollapseReplaceForm
      formType={isEdit ? 'edit' : 'create'}
      title={isEdit ? "Modifier la réunion d'information" : "Créer une réunion d'information"}
      subtitle={
        isEdit
          ? "Modifiez les informations de la réunion d'information"
          : "Renseignez les informations de la réunion d'information"
      }
      onClose={onClose}
      onSubmit={() => saveMutation.mutate()}
      submitLabel={isEdit ? 'Modifier' : 'Créer'}
      isSubmitting={saveMutation.isPending}
      isValid={isFormValid}
      isLoading={isEdit && (sessionLoading || !session)}
    >
      {isEdit && typeConfig && (
        <LabelBadge
          icon={typeConfig.icon}
          label={typeConfig.label}
          color={typeConfig.badgeColor}
          variant={typeConfig.badgeVariant}
        />
      )}
      {isTerminal && (
        <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 mb-4">
          <p className="text-sm text-yellow-800">{getErrorMessage('sessionTerminalNoEdit')}</p>
        </div>
      )}

      <div className="form-section">
        <h4 className="form-section-title">
          <Calendar className="w-4 h-4" />
          Planification
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DatePicker
            label="Date"
            required
            value={sessionDate}
            onChange={handleSessionDateChange}
            minDate={todayStr}
            disabled={isTerminal}
            inputSize="sm"
          />
          <TimePicker
            label="Heure de début"
            required
            value={startTime}
            onChange={handleStartTimeChange}
            minTime={getMinStartTimeForSessionDate(sessionDate)}
            step={1}
            disabled={isTerminal}
            inputSize="sm"
          />
          <TimePicker
            label="Heure de fin"
            value={endTime}
            onChange={setEndTime}
            minTime={getMinEndTime(startTime)}
            step={1}
            disabled={isTerminal}
            inputSize="sm"
          />
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Users className="w-4 h-4" />
          Informations générales
        </h4>
        <div>
          <Autocomplete
            key={animatorResetKey}
            label="Animateur(s)"
            placeholder="Rechercher un animateur..."
            options={animatorOptions}
            value={animatorValue}
            onChange={handleAnimatorChange}
            onSearch={onAnimatorSearch}
            isLoading={animatorsLoading}
            disabled={isTerminal}
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedAnimators.map((animator) => (
              <div
                key={animator.id}
                className="flex items-center gap-2 px-3 py-1 rounded-full border"
                style={{
                  borderColor: 'var(--color-1-nuance-3)',
                  background: 'color-mix(in srgb, var(--color-1-nuance-3) 20%, transparent)',
                }}
              >
                <span className="text-sm">{animator.label}</span>
                {!isTerminal && (
                  <button
                    type="button"
                    onClick={() => handleRemoveAnimator(animator.id)}
                    className="hover:opacity-70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {(!isEdit ? canIncludeCurrentUser : !isTerminal) && (
          <Checkbox
            label="S'inclure en tant qu'animateur"
            checked={includeCurrentUserAsAnimator}
            onCheckedChange={(checked) => setIncludeCurrentUserAsAnimator(checked)}
          />
        )}

        <Input
          label="Nombre maximum de participants"
          type="number"
          value={maxParticipants}
          onChange={(e) => setMaxParticipants(Number.parseInt(e.target.value, 10))}
          min={1}
          max={INFORMATION_MEETING_MAX_PARTICIPANTS}
          disabled={isTerminal}
          helperText="Maximum 25 participants pour un accompagnement personnalisé"
        />
        <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Participants : {selectedTutorParticipants.length}/{INFORMATION_MEETING_MAX_PARTICIPANTS}
        </div>
        <div>
          <Autocomplete
            key={tutorParticipantResetKey}
            placeholder={
              participantLimitReached
                ? getErrorMessage('participantLimitReached')
                : 'Rechercher un tuteur...'
            }
            options={tutorParticipantOptions}
            value={tutorParticipantValue}
            onChange={handleTutorParticipantChange}
            onSearch={onTutorParticipantSearch}
            isLoading={tutorParticipantsLoading}
            disabled={participantLimitReached || isTerminal}
            helperText="Seuls les tuteurs en cours d'inscription et non encore inscrits à une réunion d'information sont affichés."
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedTutorParticipants.map((tutor) => (
              <div
                key={tutor.id}
                className="flex items-center gap-2 px-3 py-1 rounded-full border"
                style={{
                  borderColor: 'var(--color-1-nuance-1)',
                  background: 'color-mix(in srgb, var(--color-1-nuance-1) 10%, transparent)',
                }}
              >
                <span className="text-sm">{tutor.label}</span>
                {!isTerminal && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTutorParticipant(tutor.id)}
                    className="hover:opacity-70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <BookOpen className="w-4 h-4" />
          Notes internes
        </h4>
        <Textarea
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          rows={3}
          placeholder="Notes pour l'équipe..."
          disabled={isTerminal}
        />
      </div>
    </CollapseReplaceForm>
  );
};
